import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import AnimatedHeading from '../../../components/AnimatedHeading';
import FadeIn from '../../../components/FadeIn';

export default function Hero() {
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const ctx = gsap.context(() => {
      const onMove = (e: MouseEvent) => {
        const { innerWidth, innerHeight } = window;
        const x = (e.clientX / innerWidth - 0.5) * 12;
        const y = (e.clientY / innerHeight - 0.5) * 12;
        gsap.to('.hero-parallax', { x, y, duration: 0.8, ease: 'power2.out' });
      };
      window.addEventListener('mousemove', onMove);
      return () => window.removeEventListener('mousemove', onMove);
    }, el);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={rootRef} className="relative min-h-screen w-full overflow-hidden bg-black text-white">
      <video
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
      >
        <source
          src="https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260423_084718_72a17915-4964-4059-afcd-22d59399b72e.mp4"
          type="video/mp4"
        />
      </video>
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/60" />

      {/* Floating decorative dot grid (parallax) */}
      <div className="hero-parallax absolute inset-0 opacity-30 pointer-events-none">
        <div className="absolute inset-0 tech-grid tech-grid-mask" />
      </div>

      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Status bar */}
        <div className="absolute top-24 left-0 right-0 px-6 md:px-12 lg:px-16 flex justify-between text-[11px] font-mono uppercase tracking-[0.3em] text-white/50">
          <FadeIn delay={1500} duration={1200}>
            <span>// vex.capital — index 026</span>
          </FadeIn>
          <FadeIn delay={1500} duration={1200}>
            <span className="hidden md:inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              fund III · now investing
            </span>
          </FadeIn>
        </div>

        <div className="flex-1 min-h-screen px-6 md:px-12 lg:px-16 flex flex-col items-center justify-center text-center">
          <div className="w-full max-w-5xl flex flex-col items-center">
            <FadeIn delay={100} duration={800}>
              <div className="mb-6 inline-flex items-center gap-2 liquid-glass px-3 py-1.5 rounded-full text-xs font-mono uppercase tracking-[0.25em] text-white/80">
                <span className="w-1.5 h-1.5 rounded-full bg-white" />
                Angel collective · est. 2019
              </div>
            </FadeIn>

            <AnimatedHeading
              text={'Shaping tomorrow\nwith vision and action.'}
              className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-normal mb-6 font-display"
              delay={200}
              charDelay={28}
            />

            <FadeIn delay={900} duration={1000}>
              <p className="text-base md:text-lg text-white/70 mb-8 max-w-2xl">
                We back visionaries and craft ventures that define what comes next —
                frontier AI, robotics, biotech, and the technologies still waiting
                to be named.
              </p>
            </FadeIn>

            <FadeIn delay={1200} duration={1000}>
              <div className="flex flex-wrap gap-3 justify-center">
                <Link
                  to="/advisory"
                  className="group bg-white text-black px-7 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
                >
                  Start a Chat
                  <i className="ri-arrow-right-up-line transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"></i>
                </Link>
                <Link
                  to="/investing"
                  className="liquid-glass border border-white/20 text-white px-7 py-3 rounded-lg font-medium hover:bg-white hover:text-black transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
                >
                  Explore Portfolio
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </FadeIn>
          </div>
        </div>

        {/* Bottom Tagline */}
        <div className="absolute bottom-0 left-0 right-0 px-6 md:px-12 lg:px-16 pb-10 lg:pb-14 flex justify-center">
          <FadeIn delay={1400} duration={1000}>
            <div className="liquid-glass border border-white/20 px-6 py-3 rounded-xl">
              <p className="text-lg md:text-xl lg:text-2xl font-light tracking-wide font-display">
                Investing · Building · Advisory
              </p>
            </div>
          </FadeIn>
        </div>

        {/* Scroll indicator */}
        <FadeIn delay={1700} duration={1200} className="absolute bottom-3 left-0 right-0 flex justify-center">
          <div className="flex flex-col items-center gap-1 text-white/40 text-[10px] font-mono uppercase tracking-[0.3em]">
            <span>scroll</span>
            <i className="ri-arrow-down-line animate-bounce"></i>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}